/**
 * Person entity validation rules.
 * Applies to all Person subtypes (Employee, Student, Contractor, etc.).
 */

import { getEntityAttrString } from './ReusableValidation';
import type { ValidationWarning } from './ReusableValidation';

function resolveSubtype(data: Record<string, any>): string {
    const et = data.EntityType ?? data.entityType ?? '';
    if (typeof et === 'string') return et.toLowerCase();
    if (et && typeof et === 'object') {
        const val = et.DisplayName || et.displayName || et.Id || et.id || '';
        return (val as string).toLowerCase();
    }
    return '';
}

const SUBTYPES_NEEDING_EMAIL = new Set(['employee', 'contractor', 'sponsored']);

export function getPersonWarnings(data: Record<string, any>): ValidationWarning[] {
    const warnings: ValidationWarning[] = [];
    const subtype = resolveSubtype(data);

    const displayName = getEntityAttrString(data, 'DisplayName');
    if (!displayName) {
        warnings.push({
            message: 'Display Name is required for person records.',
            severity: 'warning',
            attributeDisplayNames: ['DisplayName'],
        });
    }

    if (subtype !== 'vacancy') {
        const upn = getEntityAttrString(data, 'UserPrincipalName');
        if (!upn) {
            warnings.push({
                message: 'UserPrincipalName is typically required for person accounts.',
                severity: 'warning',
                attributeDisplayNames: ['UserPrincipalName'],
            });
        }
    }

    if (SUBTYPES_NEEDING_EMAIL.has(subtype)) {
        const email = getEntityAttrString(data, 'Email');
        if (!email) {
            warnings.push({
                message: 'Email address is recommended for this person type.',
                severity: 'warning',
                attributeDisplayNames: ['Email'],
            });
        }
    }

    const accountStatus = getEntityAttrString(data, 'AccountStatus');
    if (accountStatus && accountStatus.toLowerCase() !== 'active') {
        warnings.push({
            message: `Account status is "${accountStatus}".`,
            severity: 'info',
            attributeDisplayNames: ['AccountStatus'],
        });
    }

    if (subtype === 'employee' || subtype === 'contractor') {
        const endDate = getEntityAttrString(data, 'EndDate');
        if (endDate) {
            try {
                const end = new Date(endDate);
                if (!isNaN(end.getTime()) && end < new Date()) {
                    warnings.push({
                        message: 'End date is in the past. This may indicate an inactive record.',
                        severity: 'info',
                        attributeDisplayNames: ['EndDate'],
                    });
                }
            } catch {
                // ignore unparseable dates
            }
        }
    }

    return warnings;
}
